import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import Tooltip from "@/components-legacy/tooltip";
import useDeleteExportMethod from "@babackapp/private-api-client/client/use-delete-export-method";
import { ShopExportDto } from "@babackapp/private-api-client/types";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Card, CardContent } from "@babackapp/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import SimpleMenu from "@babackapp/ui/components/simple-menu";
import { TrashIcon } from "@heroicons/react/24/solid";
import { format } from "date-fns";
import { MoreHorizontal, Pencil } from "lucide-react";
import { useState } from "react";
import AddExportSettingsDialog from "./add-export-settings-dialog";
import UpdateExportSettingsDialog from "./update-export-settings-dialog";

export default function ExportSettings() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  return (
    <Card>
      <CardContent className="my-8">
        <div className="space-y-6 divide-y divide-gray-200">
          <div className="space-y-6">
            <div className="flex">
              <div className="flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {t("settings.integrations.exports.title")}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  {t("settings.integrations.exports.subtitle")}
                </p>
              </div>
              <div>
                <AddExportSettingsDialog>
                  <Button>Add Export</Button>
                </AddExportSettingsDialog>
              </div>
            </div>
          </div>

          <div className="space-y-6 pt-6">
            {(shop.exports as ShopExportDto[]).map(
              (exportConfig: ShopExportDto, indexExport) => {
                return (
                  <FTPExport
                    key={`export-${indexExport}`}
                    exportConfig={exportConfig}
                  />
                );
              },
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FTPExport(props: { exportConfig: ShopExportDto }) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutateAsync: deleteExport } = useDeleteExportMethod(shop);

  const handleDelete = async () => {
    try {
      await deleteExport({ exportId: props.exportConfig.jobId });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="flex justify-between group">
        <div className="flex space-x-4 text-sm">
          <div className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            <div className="-ml-0.5 mr-1.5 h-1.5 w-1.5 bg-green-400 rounded-full"></div>
            {props.exportConfig.type.toUpperCase()}
          </div>
          <div>{props.exportConfig.login.host}</div>
          <div className="text-gray-500">
            {`${t("settings.integrations.exports.lastRun")} ${
              props.exportConfig.lastRun > 0
                ? format(props.exportConfig.lastRun, "dd/MM/yyyy HH:mm")
                : t("misc.never")
            }`}
          </div>
          <div className="inline-flex space-x-2 items-center rounded-full px-2.5 py-0.5 text-xs font-medium text-gray-500">
            <Tooltip
              title=""
              description={
                <>
                  {props.exportConfig.filter.status.length > 0 && (
                    <p className="text-black font-semibold">
                      {props.exportConfig.filter.status.length}{" "}
                      {t("settings.integrations.exports.status")}
                    </p>
                  )}
                  {props.exportConfig.filter.status.map(
                    (subscription, index) => {
                      return (
                        <div className="mb-1" key={`status-${index}`}>
                          {t(
                            ("returnDetail.status." +
                              subscription) as Translation,
                          )}
                        </div>
                      );
                    },
                  )}
                  {props.exportConfig.filter.resolution.length > 0 && (
                    <p className="text-black font-semibold">
                      {props.exportConfig.filter.resolution.length}{" "}
                      {t("settings.integrations.exports.resolutions")}
                    </p>
                  )}
                  {props.exportConfig.filter.resolution.map(
                    (subscription, index) => {
                      return (
                        <div className="mb-1" key={`resolution-${index}`}>
                          {t(
                            ("newOrder.resolution" +
                              subscription) as Translation,
                          )}
                        </div>
                      );
                    },
                  )}
                </>
              }
            >
              <div>
                (
                {props.exportConfig.filter.status.length +
                  props.exportConfig.filter.resolution.length}{" "}
                {t("settings.integrations.exports.filters")} )
              </div>
            </Tooltip>
          </div>
        </div>

        <SimpleMenu
          iconTitle={<MoreHorizontal className="h-4 w-4" />}
          type={"neutral"}
          menu={[
            {
              onClick: () => setIsUpdating(true),
              title: t("settings.integrations.webhooks.menu.edit"),
              icon: <Pencil className="h-4 w-4 mr-1" />,
            },
            {
              onClick: () => setIsDeleting(true),
              renderTitle: () => (
                <p className="text-red-500">{t("misc.delete")}</p>
              ),
              icon: <TrashIcon className="h-4 w-4 mr-1 text-red-500" />,
            },
          ]}
        />
      </div>

      {isUpdating && (
        <UpdateExportSettingsDialog
          shopExport={props.exportConfig}
          open={isUpdating}
          onOpenChange={setIsUpdating}
        />
      )}

      {isDeleting && (
        <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("settings.integrations.exports.removeExport.title")}
              </DialogTitle>
              <DialogDescription>
                {t("settings.integrations.exports.removeExport.description")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex space-x-2 justify-end">
              <Button variant="outline" onClick={() => setIsDeleting(false)}>
                {t("misc.cancel")}
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                {t("misc.delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
